import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import AuthorIcons from '~/pages/authors/AuthorIcons';
import NewsFeedPosts from '~/pages/index/NewsFeed/NewsFeedPosts';
import getCardStyles from '~/pages/index/_utils/getCardStyles';

import Breadcrumbs from '~/components/Breadcrumbs';
import { Image } from '~/components/Image';
import Text from '~/components/Text';

import useDebounce from '~/hooks/useDebounce';
import usePaginatedAuthorsPosts from '~/hooks/useLoaders/usePaginatedAuthorPosts';

import getAuthor from '~/services/api/authors/getAuthor.server';
import getPaginatedAuthorPosts from '~/services/api/authors/getPaginatedAuthorPosts';
import getPublication from '~/services/api/publication/getPublication.server';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

import { loaderSession } from '~/session';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = data?.publication;
  const requestUrl = data?.requestUrl;

  const author = data?.author;

  if (!publication || !author) {
    return [];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(requestUrl),
    matches,
    returnEarlyCheck: !author,
    overrides: {
      title: author?.name || `${publication?.name} Authors`,
      description: author?.bio || `${publication?.description}`,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      author: {
        '@type': 'Person',
        name: author?.name,
        image: {
          '@type': 'ImageObject',
          contentUrl: author?.profile_picture?.url,
          thumbnailUrl: author?.profile_picture?.thumb?.url,
        },
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url,
              name: publication?.name,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': `${publication?.url}authors`,
              name: 'Authors',
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@id': requestUrl,
              name: author?.name,
            },
          },
        ],
      },
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }: any) => {
  const { session } = await loaderSession(request);

  const url = new URL(request.url);

  const [author, paginatedAuthorPosts, publication] = await Promise.all([
    getAuthor(request, session, params?.authorId),
    getPaginatedAuthorPosts(request, session, {
      authorId: params?.authorId || '',
      page: '1',
    }),
    getPublication(request, session),
  ]);

  return json({
    publication,
    requestUrl: url.href,
    paginatedAuthorPosts,
    author,
    cardStyles: getCardStyles({
      webTheme: publication.web_template.web_theme,
    }),
  });
};

const AuthorShow = () => {
  const { publication, author, paginatedAuthorPosts, cardStyles } = useLoaderData<typeof loader>();

  const { isSearching } = useDebounce(500);

  const { isLoading, fetchPosts, postsToRender, showLoadMore } = usePaginatedAuthorsPosts({
    initialPosts: paginatedAuthorPosts.posts,
    initialPagination: paginatedAuthorPosts.pagination,
    authorId: author.id,
  });

  const breadCrumbs = [
    {
      text: publication?.name,
      href: '../',
    },
    {
      text: 'Authors',
      href: './',
    },
    {
      text: author.name,
      href: author.slug,
    },
  ];

  return (
    <div className="px-4">
      {publication.render_breadcrumbs && (
        <div className="mx-auto mt-8 max-w-6xl py-4">
          <Breadcrumbs breadcrumbs={breadCrumbs} />
        </div>
      )}
      <div className="mx-auto flex w-full max-w-6xl pb-16 pt-8">
        <div className="flex w-full flex-col space-y-10">
          <div>
            <div className="flex w-full flex-col items-center space-x-0 space-y-8 sm:flex-row sm:space-x-8 sm:space-y-0">
              <div>
                <div className="z-10 col-span-1 h-40 min-h-40 w-40 min-w-40 overflow-hidden rounded-full">
                  <Image
                    src={author.profile_picture.url}
                    alt={author.name}
                    maxWidth={400}
                    loading="lazy"
                    aspectRatio="social"
                  />
                </div>
              </div>
              <div className="w-full max-w-2xl space-y-1">
                <Text
                  as="h1"
                  size="3xl"
                  type="title"
                  theme="default"
                  className="w-full text-center font-semibold text-wt-text-on-background sm:text-left"
                >
                  {author.name}
                </Text>
                <Text
                  as="p"
                  size="md"
                  type="body"
                  theme="default"
                  className="w-full text-center text-wt-text-on-background sm:text-left"
                >
                  {author.bio}
                </Text>
                <div className="pt-4 sm:pt-2">
                  <AuthorIcons author={author} className="flex justify-center space-x-2 sm:justify-start" />
                </div>
              </div>
            </div>
          </div>

          <NewsFeedPosts
            publication={publication}
            posts={postsToRender}
            isSearching={isSearching}
            isLoading={isLoading}
            showLoadMore={showLoadMore}
            fetchPosts={fetchPosts}
            cardStyles={cardStyles}
          />
        </div>
      </div>
    </div>
  );
};

export default AuthorShow;
